import type { ReactNode } from "react";
import type { Syrup } from "../game/coffee";
import type { Drink, Topping } from "../game/food";

// A ticket's food drawn small: the burger's patty, each topping, fries, and
// a drink in a soda cup. Each is its own colour and shape so a ticket reads
// at a glance.

export type Food = "patty" | Topping | "fries" | Drink | "shot" | "milk" | Syrup;

/** A syrup bottle with its pump. */
const bottle = (fill: string) => (
  <>
    <rect x="11" y="2" width="2" height="5" className="fill-ink" />
    <rect x="9" y="6" width="6" height="2" className="fill-ink" />
    <rect x="7" y="8" width="10" height="14" rx="2" className={fill} />
  </>
);

/** A soda cup with the drink in it. */
const cup = (liquid: string) => (
  <>
    <path d="M6 3h12l-1.6 18H7.6z" className="fill-panel stroke-steel-dark" strokeWidth="1.2" />
    <path d="M6.9 8h10.2l-1.2 12.2H8.1z" className={liquid} />
  </>
);

const SHAPES: Record<Food, ReactNode> = {
  patty: (
    <>
      <path d="M3 9a9 6 0 0 1 18 0z" className="fill-bun" />
      <rect x="2.5" y="10" width="19" height="4" rx="2" className="fill-patty" />
      <rect x="3" y="15" width="18" height="4" rx="1.5" className="fill-bun" />
    </>
  ),
  cheese: <path d="M3 8h18l-2 9H5z" className="fill-sunny" />,
  bacon: <path d="M2 10q5-4 10 0t10 0v4q-5 4-10 0t-10 0z" className="fill-bacon" />,
  lettuce: <path d="M2 12q2-5 5-2 2-5 5-1 3-4 5 1 3-3 5 2-2 6-10 6T2 12z" className="fill-lettuce" />,
  tomato: (
    <>
      <circle cx="12" cy="12" r="8" className="fill-chili" />
      <circle cx="12" cy="12" r="4.5" className="fill-danger-soft" />
    </>
  ),
  onion: (
    <>
      <circle cx="12" cy="12" r="8" className="fill-onion" />
      <circle cx="12" cy="12" r="5" className="fill-panel" />
      <circle cx="12" cy="12" r="3" className="fill-onion" />
    </>
  ),
  pickles: (
    <>
      <circle cx="8" cy="12" r="5" className="fill-pickle" />
      <circle cx="16" cy="12" r="5" className="fill-pickle" />
      <circle cx="8" cy="12" r="2" className="fill-lettuce" />
      <circle cx="16" cy="12" r="2" className="fill-lettuce" />
    </>
  ),
  shot: (
    <>
      <path d="M8 6h8l-1 14H9z" className="fill-panel stroke-steel-dark" strokeWidth="1.2" />
      <path d="M8.6 11h6.8l-0.6 8.4H9.2z" className="fill-pop" />
    </>
  ),
  milk: (
    <>
      <path d="M17 9q4 1 3 6" className="fill-none stroke-steel-dark" strokeWidth="1.6" />
      <path d="M7 5h10l1 16H6z" className="fill-steel stroke-steel-dark" strokeWidth="1.2" />
      <path d="M7.4 8h9.2l0.2 3H7.2z" className="fill-panel" />
    </>
  ),
  vanilla: bottle("fill-sunny-soft"),
  caramel: bottle("fill-yolk"),
  hazelnut: bottle("fill-coffee"),
  water: cup("fill-water"),
  pop: cup("fill-pop"),
  diet: cup("fill-coffee"),
  lemon: cup("fill-lemon"),
  fries: (
    <>
      <rect x="6" y="3" width="2.5" height="11" className="fill-yolk" />
      <rect x="10.5" y="2" width="2.5" height="12" className="fill-yolk" />
      <rect x="15" y="4" width="2.5" height="10" className="fill-yolk" />
      <path d="M4 10h16l-2 11H6z" className="fill-chili" />
    </>
  ),
};

export function FoodIcon({ food, size = 20 }: { food: Food; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
      {SHAPES[food]}
    </svg>
  );
}
